import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

// Finger / mouse / stylus signature pad.
// ref API: isEmpty(), clear(), toPngBytes() → trimmed transparent PNG (Uint8Array) or null,
//          loadDataUrl(url) → draws a saved signature back onto the pad.
const SignaturePad = forwardRef(function SignaturePad(
  { ink = '#16246b', height = 180, background = '#fafaf7', placeholder = 'חתמו כאן', onChange },
  ref
) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const [empty, setEmpty] = useState(true);

  const setupCanvas = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = Math.round(rect.width * dpr);
    c.height = Math.round(rect.height * dpr);
    const ctx = c.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = ink;
    ctx.fillStyle = ink;
  }, [ink]);

  useEffect(() => {
    setupCanvas();
    // Resizing clears the canvas, so only re-setup while it's still empty.
    const onResize = () => { if (empty) setupCanvas(); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [setupCanvas, empty]);

  const markDirty = () => {
    if (empty) { setEmpty(false); onChange && onChange(false); }
  };

  const pos = e => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top, p: e.pressure || 0.5 };
  };

  const down = e => {
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    drawing.current = true;
    last.current = pos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.arc(last.current.x, last.current.y, 1.1, 0, Math.PI * 2);
    ctx.fill();
    markDirty();
  };
  const move = e => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = pos(e);
    const ctx = canvasRef.current.getContext('2d');
    const dist = Math.hypot(p.x - last.current.x, p.y - last.current.y);
    ctx.lineWidth = Math.max(1.4, Math.min(3, 3.2 - dist * 0.08));
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };
  const up = () => { drawing.current = false; };

  useImperativeHandle(ref, () => ({
    isEmpty: () => empty,
    clear: () => {
      const c = canvasRef.current;
      c.getContext('2d').clearRect(0, 0, c.width, c.height);
      setupCanvas();
      setEmpty(true);
      onChange && onChange(true);
    },
    loadDataUrl: url => new Promise(resolve => {
      const img = new Image();
      img.onload = () => {
        const c = canvasRef.current;
        const r = c.getBoundingClientRect();
        const ratio = Math.min((r.width - 20) / img.width, (r.height - 20) / img.height, 1);
        const w = img.width * ratio, h = img.height * ratio;
        c.getContext('2d').drawImage(img, (r.width - w) / 2, (r.height - h) / 2, w, h);
        setEmpty(false);
        onChange && onChange(false);
        resolve();
      };
      img.onerror = () => resolve();
      img.src = url;
    }),
    toDataUrl: () => trimmed()?.toDataURL('image/png') || null,
    toPngBytes: () => {
      const t = trimmed();
      if (!t) return null;
      const bin = atob(t.toDataURL('image/png').split(',')[1]);
      const out = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
      return out;
    },
  }));

  // Crops to the drawn strokes so the signature scales nicely onto the PDF.
  function trimmed() {
    const c = canvasRef.current;
    if (!c || empty) return null;
    const ctx = c.getContext('2d');
    const { width, height } = c;
    const data = ctx.getImageData(0, 0, width, height).data;
    let minX = width, minY = height, maxX = -1, maxY = -1;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (data[(y * width + x) * 4 + 3] > 8) {
          if (x < minX) minX = x; if (x > maxX) maxX = x;
          if (y < minY) minY = y; if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) return null;
    const pad = 6;
    minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
    maxX = Math.min(width - 1, maxX + pad); maxY = Math.min(height - 1, maxY + pad);
    const out = document.createElement('canvas');
    out.width = maxX - minX + 1;
    out.height = maxY - minY + 1;
    out.getContext('2d').drawImage(c, minX, minY, out.width, out.height, 0, 0, out.width, out.height);
    return out;
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: `${height}px`, background, borderRadius: '6px', overflow: 'hidden' }}>
      <canvas
        ref={canvasRef}
        aria-label="אזור חתימה"
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        onPointerLeave={up}
        style={{ width: '100%', height: '100%', display: 'block', touchAction: 'none', cursor: 'crosshair' }}
      />
      <div style={{ position: 'absolute', left: '16px', right: '16px', bottom: '34px', borderBottom: '1px solid rgba(0,0,0,0.25)', pointerEvents: 'none' }} />
      {empty && (
        <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', color: 'rgba(0,0,0,0.28)', fontSize: '15px', fontFamily: "'Frank Ruhl Libre', serif" }}>
          {placeholder}
        </span>
      )}
    </div>
  );
});

export default SignaturePad;
